import moment from "moment-timezone";

// The OMS reports D2C dates as IST calendar days and timestamps as ISO strings.
export const IST = "Asia/Kolkata";
export const SCAN_STALE_MINUTES = 10;
export const OUTBOX_STALE_MINUTES = 15;

const EMPTY = "-";

const isBlank = (value) => value === null || value === undefined || value === "";

export const formatNumber = (value) =>
  isBlank(value) ? EMPTY : Number(value).toLocaleString("en-IN");

export const formatRupees = (value) =>
  isBlank(value)
    ? EMPTY
    : `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Assumption until the ops API ships: offered_pct, acceptance_rate and
// fulfilment_rate arrive as percentages (42.9), not fractions (0.429).
// Change this one function if the server sends fractions.
export const formatPct = (value) =>
  isBlank(value) ? EMPTY : `${Number(value).toFixed(1)}%`;

export const formatMinutes = (value) =>
  isBlank(value) ? EMPTY : `${Math.round(Number(value))} min`;

export const formatHours = (value) =>
  isBlank(value) ? EMPTY : `${Number(value).toFixed(1)} h`;

export const formatKm = (value) =>
  isBlank(value) ? EMPTY : `${Number(value).toFixed(1)} km`;

export const formatDateTime = (iso) =>
  isBlank(iso) ? EMPTY : moment(iso).tz(IST).format("DD MMM YYYY, HH:mm");

export const humaniseCode = (code) => {
  if (isBlank(code)) return EMPTY;
  const words = String(code).replace(/_/g, " ").toLowerCase().trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// The date picker hands back local calendar days; the API wants YYYY-MM-DD.
// "12 min ago", "3 h ago", "2 days ago", or "in 12 min" for a time ahead.
export const formatRelative = (iso, now = new Date()) => {
  if (isBlank(iso)) return EMPTY;
  const at = moment(iso);
  if (!at.isValid()) return EMPTY;
  const minutes = Math.round(moment(now).diff(at, "minutes", true));
  const size = Math.abs(minutes);
  if (size < 1) return "just now";
  let text;
  if (size < 60) text = `${size} min`;
  else if (size < 48 * 60) text = `${Math.round(size / 60)} h`;
  else text = `${Math.round(size / (24 * 60))} days`;
  return minutes > 0 ? `${text} ago` : `in ${text}`;
};

export const isPast = (iso, now = new Date()) => !isBlank(iso) && moment(iso).isBefore(now);

// Badge tone per activity stage: waiting amber, in progress blue, delivered
// green, returned or cancelled grey (the plain pill).
const STAGE_TONES = {
  WAITING_FOR_DEALER: "warn",
  ACCEPTED: "info",
  FRAME_RESERVED: "info",
  PDI_DONE: "info",
  OUT_FOR_DELIVERY: "info",
  DELIVERED: "ok",
  RETURNED_TO_CENTRAL: "",
  CANCELLED: "",
};
export const stageTone = (stage) => STAGE_TONES[stage] || "";

// Calendar days, counting both ends, that the ops API accepts in one request.
export const ORDERS_MAX_DAYS = 31;
export const SUMMARY_MAX_DAYS = 92;

// Calendar days from one YYYY-MM-DD to another, counting both ends.
export const rangeDays = (from, to) => {
  if (!from || !to) return 0;
  return moment(to, "YYYY-MM-DD").diff(moment(from, "YYYY-MM-DD"), "days") + 1;
};

export const toIstDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

// A local Date for the IST calendar day `days` before `now`, for the picker.
export const istDaysAgo = (days, now = new Date()) => {
  const ist = moment(now).tz(IST).subtract(days, "days");
  return new Date(ist.year(), ist.month(), ist.date());
};

export const minutesSince = (iso, now = new Date()) =>
  isBlank(iso) ? null : (now.getTime() - new Date(iso).getTime()) / 60000;

export const isScanStale = (lastScanAt, now = new Date()) => {
  const age = minutesSince(lastScanAt, now);
  return age === null || age > SCAN_STALE_MINUTES;
};

export const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (!isBlank(value)) query.append(key, value);
  });
  const text = query.toString();
  return text ? `?${text}` : "";
};

// Sort a copy of `rows` by `key`. Empty values always sink to the bottom.
export const sortRows = (rows, key, direction) => {
  const factor = direction === "desc" ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const left = a?.[key];
    const right = b?.[key];
    if (isBlank(left) && isBlank(right)) return 0;
    if (isBlank(left)) return 1;
    if (isBlank(right)) return -1;
    if (typeof left === "number" && typeof right === "number") {
      return (left - right) * factor;
    }
    return String(left).localeCompare(String(right), "en", { sensitivity: "base" }) * factor;
  });
};

// An object of reason -> count as rows for a bar list, biggest first.
export const countsToRows = (counts) =>
  Object.entries(counts || {})
    .map(([code, count]) => ({ code, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);

// Traffic-light tones for the System panel: "bad" (red), "warn" (amber), "ok".
const OUTBOX_WARN_MINUTES = 5;
const worst = (tones) => (tones.includes("bad") ? "bad" : tones.includes("warn") ? "warn" : "ok");

export const systemTones = (system, now = new Date()) => {
  const s = system || {};
  const oldest = isBlank(s.oldest_pending_minutes) ? null : Number(s.oldest_pending_minutes);
  const failedCount = Object.values(s.failed_by_kind || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
  const tones = {
    lastScan: isScanStale(s.last_scan_at, now) ? "bad" : "ok",
    oldestPending:
      oldest === null ? "ok" : oldest > OUTBOX_STALE_MINUTES ? "bad" : oldest > OUTBOX_WARN_MINUTES ? "warn" : "ok",
    failed: failedCount > 0 ? "bad" : "ok",
    heldWithoutDecision: Number(s.held_without_decision) > 0 ? "bad" : "ok",
  };
  return { ...tones, overall: worst(Object.values(tones)) };
};

export const erpTones = (erp) => {
  const e = erp || {};
  const tones = {
    failed: Number(e.failed) > 0 ? "bad" : "ok",
    notStarted: Number(e.not_started) > 0 ? "warn" : "ok",
  };
  return { ...tones, overall: worst(Object.values(tones)) };
};
